import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import RangeHeroOverview from './RangeHeroOverview/RangeHeroOverview';
import RangeEditorial from './RangeEditorial/RangeEditorial';
import ProductRecommendation from '../../components/ProductRecommendation/ProductRecommendation';
import RangeTreatments from './RangeTreatments/RangeTreatments';
import ProductBenefits from '../../components/ProductBenefits/ProductBenefits';
import ProductIngredients from '../../components/ProductIngredients/ProductIngredients';
import ProductReviews from '../../components/ProductReviews/ProductReviews';
import StylesLooksHowto from '../../components/StylesLooksHowto/StylesLooksHowto';
import ColorCharts from './ColorCharts/ColorCharts';
import HowToPanel from './HowToPanel/HowToPanel';
import AdditionalVideo from './AdditionalVideo/AdditionalVideo';
import CareMenuSection from './CareMenuSection/CareMenuSection';
import AccordionSection from './AccordionSection/AccordionSection';
import ProfileSection from './ProfileSection/ProfileSection';
import SalonListCTA from './SalonListCTA/SalonListCTA';

import QuickLinksSection from '../../components/QuickLinksSection';
import Placeholder from '../../components/Placeholder';
import Section from '../../components/Section';
import Video from '../../components/BrightcoveVideo';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import { TABLET_P } from '../../constants';

/**
 * CMS で QUICK LINK LABEL が設定されたものだけコレクションし、ページ内リンクさせるようにする
 */
const getQuickLinks = data => {
  const links = [];
  // PRODUCTS メニューを一番最後に配置させるためのキャッシュ用
  let cachedProductsMenu;

  // 配列のみをコレクションする
  const typeOfArrayItems = _.reduce(
    data,
    (accumulator, currentValue, key) => (Array.isArray(data[key]) ? [...accumulator, data[key]] : accumulator),
    []
  );

  if (typeOfArrayItems.length === 0) return false;

  // CMS で QUICK LINK LABEL が設定されたものだけコレクションする
  _.forEach(typeOfArrayItems, node => {
    _.forEach(node, item => {
      if (item.hasOwnProperty.call(item, 'quickLinkLabel')) {
        if (item.quickLinkLabel !== 'PRODUCTS') {
          links.push({ id: item.entryId, label: item.quickLinkLabel });
        } else {
          // PRODUCTS メニューをキャッシュしておく
          cachedProductsMenu = { id: item.entryId, label: item.quickLinkLabel };
        }
      }
    });
  });

  // PRODUCTS メニューを一番最後に配置させる
  links.push(cachedProductsMenu);

  return links;
};

/**
 * 商品ラインナップ紹介画面
 *   表示内容は、主に次の2パターンがある
 *     1. カテゴリ > サブカテゴリ
 *     2. カテゴリ > サブカテゴリ > LINE
 */
const RangePage = ({ copy, data, productCopy, locale, location, menuInfo }) => {
  // パンくずリスト > カテゴリ表示用 (サブカテゴリがある場合に使用する)
  let labelWithSubCategory = null;
  // パンくずリスト > カテゴリ表示用 (サブカテゴリがない場合に使用する)
  let labelWithoutSubCategory = null;
  // パンくずリスト > サブカテゴリ表示用 (パンくずに LINE を伴う場合)
  let subCategoryLabelWithLine = null;
  // パンくずリスト > サブカテゴリ表示用 (パンくずに LINE を伴わない場合)
  let subCategoryLabelWithoutLine = null;
  // サブカテゴリのパスを設定するのに使用する
  let subCategoryPath = null;

  if (!data) return;

  _.forEach(_.get(menuInfo, 'children'), ({ label, children }) => {
    _.forEach(children, item => {
      // 該当: サブカテゴリを持たないもの
      // 該当する例: HAIR COLOR > COLORMUSE
      if (!_.get(item, 'children')) {
        if (
          !!(_.get(item, 'label') && _.get(data, 'key') && item.label.toLowerCase() === data.key.toLowerCase()) ||
          !!(_.get(item, 'slug') && _.get(data, 'slug') && item.slug.toLowerCase() === data.slug.toLowerCase())
        ) {
          labelWithoutSubCategory = label.toUpperCase();
        }
      }
      // 該当: サブカテゴリを持つもの
      else {
        if (!_.get(data, 'slug')) {
          return;
        }

        // 該当 1: ヘッダー直下のメニューから、サブカテゴリを選択した場合
        // 該当 2: HAIR CARE > SUBLIMIC、MEN > THE GROOMING
        if (_.get(item, 'key') && _.get(data, 'key') && item.key.toLowerCase() === data.key.toLowerCase()) {
          subCategoryLabelWithoutLine = item.key.toUpperCase();

          // TODO: 注意事項 1: 現時点 (2019年11月8日) で、slug を持つものと持たないもの、且つ、url を持つものと持たないものがあるので注意すること。
          // TODO: 注意事項 2: 例えば、SUBLIMIC は slug を持たない、且つ、url を持つ。COLORMUSE は slug を持つ、且つ、url を持たない。

          // 商品情報からサブカテゴリのパスを取得する (URL 直打ち対策 (react-router-dom > Link による遷移時にパラメータを設定することは可能だが、キャッシュクリア、且つ、直打ちされると、値が取得出来ないリスクがあるため))
          subCategoryPath = _.get(item, 'url') || _.get(item, 'slug');
          labelWithSubCategory = label.toUpperCase();
        } else {
          // 該当 1: ヘッダー直下のメニューから、LINE を選択した場合
          // 該当 2: サブカテゴリを持つ、且つ、siteOptions.menu の中にある個々の LINE が slug を持つもの
          // 該当する例 1: HAIR CARE > SUBLIMIC > SUBLIMIC AQUAINTENSIVE
          // 該当する例 2: MEN > HAIR & SCALP CARE
          // 該当しない例 1: THE HAIR CARE > FUENTE FORTE
          // 該当しない例 2: HAIR COLOR > COLORMUSE
          if (_.find(item.children, ({ slug }) => slug === data.slug)) {
            labelWithSubCategory = label.toUpperCase();
            subCategoryLabelWithLine =
              item.label.toUpperCase() || (!!_.get(data, 'subCategory') && data.subCategory.toUpperCase());
            subCategoryPath = _.get(item, 'url') || _.get(item, 'slug');
          }
          // 該当: サブカテゴリを持つ、且つ、siteOptions.menu の中にある個々の LINE が slug を持たないもの
          else {
            switch (data.slug) {
              case 'the-hair-care':
                if (_.get(item, 'key') === 'THE HAIR CARE') {
                  labelWithSubCategory = 'HAIR CARE';
                  subCategoryPath = '/range/hair-care/the-hair-care';
                }
                break;
              case 'adenovital':
              case 'airy-flow':
              case 'aqua-intensive':
              case 'ca-ff':
              case 'fuente-forte':
              case 'luminoforce':
              case 'luminogenic':
              case 'sleekliner':
                subCategoryLabelWithLine = !!_.get(data, 'subCategory') && data.subCategory.toUpperCase();
                // 判定を行う理由: SKIN/BODY CARE カテゴリも、ADENOVITAL を含むため。
                labelWithSubCategory = (labelWithoutSubCategory || 'hair care').toUpperCase();
                subCategoryPath = '/range/hair-care/the-hair-care';
                break;
              default:
                break;
            }
          }
        }
      }
    });
  });

  const isEnabled = !!(locale && location && data && _.get(data, 'name'));

  const firstProduct =
    data.productLists && data.productLists[0].products ? data.productLists[0].products[0] : undefined;

  const quickLinks = getQuickLinks(data);

  // パンくずリスト表示内容の配列
  let breadCrumbMenuItems = [];

  // サブカテゴリを持たない場合
  if (isEnabled && !subCategoryPath && labelWithoutSubCategory) {
    // TOP > PRODUCTS > カテゴリ > LINE
    breadCrumbMenuItems = [
      {
        name: 'TOP',
        path: `/${locale}/`,
      },
      // メニュー名を設定する (PRODUCTS)
      {
        name: menuInfo.label,
        path: null,
      },
      // カテゴリ名を設定する (例) HAIR CARE)
      {
        name: labelWithoutSubCategory,
        path: null,
      },
      // LINE 名を設定する (例) AQUA INTENSIVE)
      {
        name: data.name.toUpperCase(),
        path: null,
      },
    ];
  }

  // サブカテゴリを持つ場合
  if (isEnabled && subCategoryPath && labelWithSubCategory) {
    if (!!(!_.get(location, 'state.hasLine') && !subCategoryLabelWithLine) || subCategoryLabelWithoutLine) {
      // TOP > PRODUCTS > カテゴリ > サブカテゴリ
      breadCrumbMenuItems = [
        {
          name: 'TOP',
          path: `/${locale}/`,
        },
        // メニュー名を設定する (PRODUCTS)
        {
          name: menuInfo.label,
          path: null,
        },
        // カテゴリ名を設定する (例) HAIR CARE)
        {
          // 判定を行う理由: SKIN/BODY CARE カテゴリも、ADENOVITAL を含むため。
          name: labelWithoutSubCategory || labelWithSubCategory,
          path: null,
        },
        // サブカテゴリ名を設定する (例) SUBLIMIC)
        {
          name: subCategoryLabelWithoutLine || data.name.toUpperCase(),
          path: null,
        },
      ];
    } else {
      // TOP > PRODUCTS > カテゴリ > サブカテゴリ > LINE
      breadCrumbMenuItems = [
        {
          name: 'TOP',
          path: `/${locale}/`,
        },
        // メニュー名を設定する (PRODUCTS)
        {
          name: menuInfo.label,
          path: null,
        },
        // カテゴリ名を設定する (例) HAIR CARE)
        {
          name: labelWithSubCategory,
          path: null,
        },
        // サブカテゴリ名を設定する (例) SUBLIMIC)
        {
          name: (subCategoryLabelWithLine || data.subCategory).toUpperCase(),
          path: `/${locale}${subCategoryPath}`,
        },
        // LINE 名を設定する (例) AQUA INTENSIVE)
        {
          name: data.name.toUpperCase(),
          path: null,
        },
      ];
    }
  }

  const contentTypeToUrl = {
    productRangeSkinBodyCare: 'skin-body-care',
    productRangeHairCare: 'hair-care',
    productRangeHairColor: 'hair-color',
    productRangeMakeUp: 'make-up',
    productRangeMens: 'mens',
    productRangePerm: 'perm',
    productRangeStyling: 'styling',
  };

  return (
    <React.Fragment>
      {_.get(data, 'slug') && (
        <Helmet
          link={[
            makeCanonicalTagObj(locale, `/range/${contentTypeToUrl[data.contentType]}/${data.slug}`),
            ...makeHreflangTagArr(data.region, `/range/${contentTypeToUrl[data.contentType]}/${data.slug}`),
          ]}
        />
      )}
      {_.get(data, 'metaData') && (
        <Helmet
          title={_.get(data.metaData, 'title')}
          meta={[
            {
              name: 'description',
              content: _.get(data.metaData, 'description'),
            },
            {
              name: 'keyword',
              content: _.get(data.metaData, 'keyword'),
            },
            // TODO: OGP タグは一旦保留 (2020年01月07日)
            // {
            //   property: 'og:url',
            //   content: _.get(data.metaData, 'url'),
            // },
            // {
            //   property: 'og:type',
            //   content: _.get(data.metaData, 'type'),
            // },
            // {
            //   property: 'og:title',
            //   content: _.get(data.metaData, 'title'),
            // },
            // {
            //   property: 'og:image',
            //   content: _.get(data.metaData, 'image.file.url'),
            // },
            // {
            //   property: 'og:description',
            //   content: _.get(data.metaData, 'description'),
            // },
            // {
            //   property: 'og:site_name',
            //   content: _.get(data.metaData, 'siteName'),
            // },
          ]}
        />
      )}
      {/* サブメニュー (「ABOUT」「SPECIAL RECIPE」など) */}
      {quickLinks && quickLinks.length > 1 && <QuickLinksSection links={quickLinks} />}

      {/* メイン画像、タイトル、説明文表示、「商品を見る」「取扱サロン一覧」ボタン */}
      <RangeHeroOverview
        copy={copy}
        image={data.heroImages && data.heroImages[0]}
        product={data.name}
        description={data.description}
        targetConditions={data.targetConditions}
        hasProducts={data.notLoaded || (data.productLists && data.productLists.some(list => list.products.length))}
        salonListCta={data.salonListCta}
        locale={locale}
        quickLinks={quickLinks}
        location={location}
        productLists={data.productLists}
      />

      {(data.notLoaded || data.heroVideo) && (
        <Section options={{ content: { locked: true } }}>
          {data.heroVideo ? <Video {...data.heroVideo} /> : <Placeholder w="100%" pb="56.25%" />}
        </Section>
      )}

      {(data.notLoaded || data.additionalVideo) && (
        <Section options={{ content: { locked: true } }}>
          {data.additionalVideo ? (
            <AdditionalVideo data={data.additionalVideo} />
          ) : (
            <Placeholder w="100%" pb="56.25%" />
          )}
        </Section>
      )}

      {/* サブメニュー「ABOUT」「SPECIAL RECIPE」の内容 */}
      {(data.notLoaded || data.editorials) &&
        data.editorials.map(editorial => (
          <RangeEditorial
            key={editorial.entryId || editorial.key}
            locale={locale}
            description={editorial.description}
            entryId={editorial.entryId}
            title={editorial.title}
            topics={editorial.topics}
            image={editorial.editorialImage}
            oneImage={editorial.oneImage}
            column={editorial.column}
            link={editorial.link}
            quickLinks={quickLinks}
            additionalText={_.get(editorial, 'additionalText') || null}
          />
        ))}

      {(data.notLoaded || data.careMenuSection) && (
        <Section options={{ content: { locked: true } }}>
          {data.careMenuSection ? <CareMenuSection data={data.careMenuSection} /> : <Placeholder w="100%" h="50px" />}
        </Section>
      )}

      {/* サブメニュー「COLOR CHART」の内容 */}
      {(data.notLoaded || (data.contentType === 'productRangeHairColor' && data.colorChartsWithDescription)) && (
        <Section options={{ content: { locked: true } }}>
          {data.colorChartsWithDescription && <ColorCharts copy={copy} data={data.colorChartsWithDescription} />}
        </Section>
      )}

      {(data.notLoaded || data.howToPanels) && (
        <Section options={{ content: { locked: true } }}>
          {data.howToPanels ? <HowToPanel data={data.howToPanels} /> : ''}
        </Section>
      )}

      {(data.notLoaded || data.accordionSection) && (
        <Section options={{ content: { locked: true } }}>
          {data.accordionSection ? (
            <AccordionSection data={data.accordionSection} />
          ) : (
            <Placeholder w="100%" h="50px" />
          )}
        </Section>
      )}

      {(data.notLoaded || data.profileSection) && (
        <Section options={{ content: { locked: true } }}>
          {data.profileSection ? <ProfileSection data={data.profileSection} /> : <Placeholder w="100%" h="100px" />}
        </Section>
      )}

      {(data.notLoaded ||
        (data.contentType === 'productRangeSkinBodyCare' && firstProduct && firstProduct.keyIngredients)) && (
        <ProductIngredients keyIngredients={firstProduct.keyIngredients} />
      )}

      {(data.notLoaded ||
        (data.contentType === 'productRangeSkinBodyCare' && firstProduct && firstProduct.benefits)) && (
        <ProductBenefits benefits={firstProduct.benefits} extended={false} />
      )}

      {(data.notLoaded ||
        (data.contentType === 'productRangeSkinBodyCare' && firstProduct && firstProduct.reviews)) && (
        <ProductReviews reviews={firstProduct.reviews} />
      )}

      {(data.notLoaded || (data.contentType === 'productRangeSkinBodyCare' && data.instruction)) && (
        <StylesLooksHowto procedure={[{ name: 'Learn the Look', steps: data.instruction }]} />
      )}

      {(data.notLoaded ||
        (data.contentType === 'productRangeHairCare' &&
          (data.treatments || data.treatmentImage || data.treatmentDescription))) && (
        <RangeTreatments
          copy={copy}
          treatments={data.treatments}
          image={data.treatmentImage}
          description={data.treatmentDescription}
        />
      )}

      {/* COLORMUSE >「PRODUCTS」、SUBLIMIC > 「PRODUCT LINES」の内容 */}
      {(data.notLoaded || data.productLists) &&
        data.productLists.map((list, idx) => (
          <React.Fragment>
            {!!list.products.length && (
              <ProductRecommendation
                id={idx === 0}
                entryId={list.entryId}
                key={list.entryId || list.key}
                copy={{ ...productCopy, accompany_with: list.productType }}
                relatedProducts={list.products}
                labelId={data.notLoaded ? [] : list.productType.toLowerCase()}
                location={location}
                ancestorName={_.get(location, 'state.ancestor') || null}
                parentName={_.get(location, 'state.hasLine') ? location.state.parent : data.name}
                parentPath={_.get(location, 'state.hasLine') ? location.state.prevPath : null}
                brotherName={_.get(location, 'state.hasLine') ? data.name : null}
              />
            )}
          </React.Fragment>
        ))}

      {/* 「取扱サロン一覧」ボタン */}
      {(data.notLoaded || data.bottomLink) && <SalonListCTA data={data.bottomLink} locale={locale} />}

      {/* パンくずリスト */}
      {breadCrumbMenuItems.length > 0 ? (
        <BreadCrumbMenu currentPath={window.location.href} items={breadCrumbMenuItems} />
      ) : (
        <Placeholder w="200px" h="24px" m={window.innerWidth > TABLET_P ? '0 0 20px 40px' : '0 0 20px 20px'} />
      )}
    </React.Fragment>
  );
};

RangePage.defaultProps = {
  copy: {},
  data: {
    notLoaded: true,
    editorials: [
      { key: 1, topics: [{ key: 1 }, { key: 2 }, { key: 3 }] },
      { key: 2, topics: [{ key: 1 }, { key: 2 }, { key: 3 }] },
    ],
    instruction: [{ key: 1 }, { key: 2 }, { key: 3 }],
    productLists: [
      {
        key: 1,
        products: [
          { key: 1, keyIngredients: [{ key: 1 }, { key: 2 }, { key: 3 }] },
          { key: 2 },
          { key: 3 },
          { key: 4 },
        ],
      },
      { key: 2, products: [{ key: 1 }, { key: 2 }, { key: 3 }, { key: 4 }] },
    ],
    treatments: [{ key: 1 }, { key: 2 }, { key: 3 }],
  },
  productCopy: {},
};

RangePage.propTypes = {
  copy: PropTypes.shape(),
  locale: PropTypes.string.isRequired,
  location: PropTypes.shape({}).isRequired,
  data: PropTypes.shape(),
  // 商品情報 (パンくずリストに必要な情報を含む)
  menuInfo: PropTypes.shape({}).isRequired,
  productCopy: PropTypes.shape(),
};

const mapState = state => ({
  copy: state.siteOptions.siteCopyRangePage,
  locale: state.locale,
  // 商品情報をキャッシュしておく
  menuInfo: state.siteOptions.menu ? state.siteOptions.menu[3] : {},
  productCopy: state.siteOptions.siteCopyProductPage,
});

export default connect(mapState)(RangePage);



// WEBPACK FOOTER //
// ./src/pages/RangePage/RangePage.js